import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth-context";
import {
  listStaffDirectory,
  listStaffMessages,
  markStaffMessageRead,
  sendStaffMessage,
} from "@/lib/staff-messages.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/staff")({
  head: () => ({
    meta: [
      { title: "Team directory — Quibblies" },
      {
        name: "description",
        content: "Admin-only directory of Quibblies team members with private staff messaging.",
      },
      { property: "og:title", content: "Team directory — Quibblies" },
      { property: "og:description", content: "Admin-only staff directory and messaging." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: StaffDirectoryPage,
});

const ROLE_FILTERS = [
  { id: "all", label: "All roles" },
  { id: "owner", label: "Owner" },
  { id: "administrator", label: "Administrator" },
  { id: "moderator", label: "Moderator" },
];

const VERIFICATION_FILTERS = [
  { id: "all", label: "Any status" },
  { id: "verified", label: "Email verified" },
  { id: "unverified", label: "Unverified" },
  { id: "2fa", label: "2FA on" },
  { id: "no2fa", label: "No 2FA" },
];

type StaffMessage = {
  id: string;
  thread_id: string;
  parent_id: string | null;
  sender_id: string;
  recipient_id: string;
  subject: string;
  body: string;
  read_at: string | null;
  created_at: string;
};

function StaffDirectoryPage() {
  const { user, isAdmin, isOwner } = useAuth();
  const queryClient = useQueryClient();
  const loadDirectory = useServerFn(listStaffDirectory);
  const loadMessages = useServerFn(listStaffMessages);
  const send = useServerFn(sendStaffMessage);
  const markRead = useServerFn(markStaffMessageRead);

  const [recipientId, setRecipientId] = React.useState<string | null>(null);
  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState("all");
  const [verificationFilter, setVerificationFilter] = React.useState("all");
  const [replyThread, setReplyThread] = React.useState<string | null>(null);
  const [replyBody, setReplyBody] = React.useState("");

  const allowed = isAdmin || isOwner;

  const directory = useQuery({
    queryKey: ["staff-directory"],
    queryFn: () => loadDirectory(),
    enabled: allowed,
    retry: false,
  });
  const messages = useQuery({
    queryKey: ["staff-messages"],
    queryFn: () => loadMessages(),
    enabled: allowed,
    retry: false,
  });

  const sendMutation = useMutation({
    mutationFn: () => send({ data: { recipientId: recipientId!, subject, body } }),
    onSuccess: () => {
      toast.success("Message sent");
      setSubject("");
      setBody("");
      void queryClient.invalidateQueries({ queryKey: ["staff-messages"] });
    },
    onError: (error: Error) => toast.error(error.message || "Could not send that message"),
  });

  const replyMutation = useMutation({
    mutationFn: (input: {
      recipientId: string;
      subject: string;
      threadId: string;
      parentId: string;
    }) => send({ data: { ...input, body: replyBody } }),
    onSuccess: () => {
      toast.success("Reply sent");
      setReplyBody("");
      setReplyThread(null);
      void queryClient.invalidateQueries({ queryKey: ["staff-messages"] });
    },
    onError: (error: Error) => toast.error(error.message || "Could not send that reply"),
  });

  const readMutation = useMutation({
    mutationFn: (id: string) => markRead({ data: { id } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["staff-messages"] });
      void queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
    },
  });

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <EmptyState
          icon={ShieldCheck}
          title="Administrators only"
          description="This page is limited to Administrators and the Owner."
        />
      </div>
    );
  }

  const staff = directory.data ?? [];
  const recipient = staff.find((member) => member.user_id === recipientId) ?? null;
  const nameFor = (id: string) =>
    staff.find((member) => member.user_id === id)?.display_name ?? "Team member";

  const needle = query.trim().toLowerCase();
  const visibleStaff = staff.filter((member) => {
    if (roleFilter !== "all" && !member.roles.includes(roleFilter)) return false;
    if (verificationFilter === "verified" && !member.email_verified) return false;
    if (verificationFilter === "unverified" && member.email_verified) return false;
    if (verificationFilter === "2fa" && !member.two_factor_enabled) return false;
    if (verificationFilter === "no2fa" && member.two_factor_enabled) return false;
    if (!needle) return true;
    return [member.display_name, member.username, member.email ?? ""]
      .join(" ")
      .toLowerCase()
      .includes(needle);
  });

  // Group flat messages into conversations so replies read as a history.
  const rows = (messages.data ?? []) as StaffMessage[];
  const threads = Object.values(
    rows.reduce<Record<string, StaffMessage[]>>((acc, message) => {
      (acc[message.thread_id] ??= []).push(message);
      return acc;
    }, {}),
  ).sort(
    (a, b) =>
      new Date(b[b.length - 1]!.created_at).getTime() -
      new Date(a[a.length - 1]!.created_at).getTime(),
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Team directory"
        description="Everyone with a staff role, plus private conversations between team members."
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <section>
          <div className="rounded-2xl border border-border/70 bg-surface/40 p-4">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name, handle or email"
                aria-label="Search team members"
              />
            </label>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ROLE_FILTERS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setRoleFilter(option.id)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-smooth",
                    roleFilter === option.id
                      ? "border-primary/60 bg-primary/10 text-foreground"
                      : "border-border/70 text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {VERIFICATION_FILTERS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setVerificationFilter(option.id)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-smooth",
                    verificationFilter === option.id
                      ? "border-primary/60 bg-primary/10 text-foreground"
                      : "border-border/70 text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Showing {visibleStaff.length} of {staff.length} team members.
            </p>
          </div>

          {directory.isLoading ? (
            <div className="mt-4 space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-20 rounded-2xl" />
              ))}
            </div>
          ) : visibleStaff.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                icon={Users}
                title={staff.length === 0 ? "No team members yet" : "Nothing matches those filters"}
                description={
                  staff.length === 0
                    ? "Grant a staff role from Team management."
                    : "Try a different role, status or search term."
                }
              />
            </div>
          ) : (
            <ul className="mt-4 space-y-3">
              {visibleStaff.map((member) => (
                <li
                  key={member.user_id}
                  className={cn(
                    "flex flex-wrap items-center gap-4 rounded-2xl border border-border/70 bg-gradient-card p-4",
                    recipientId === member.user_id && "border-primary/60",
                  )}
                >
                  <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-border bg-accent text-sm font-semibold text-accent-foreground">
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      member.display_name.slice(0, 1).toUpperCase()
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {member.display_name}
                      {member.is_me ? " (you)" : ""}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      @{member.username}
                      {member.email ? ` · ${member.email}` : ""}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {member.roles.map((role) => (
                        <Badge key={role} variant="outline">
                          {role}
                        </Badge>
                      ))}
                      <Badge variant="outline">{member.status}</Badge>
                      <Badge variant="outline">
                        {member.email_verified ? "Email verified" : "Email unverified"}
                      </Badge>
                      {member.two_factor_enabled ? <Badge variant="outline">2FA on</Badge> : null}
                    </div>
                  </div>
                  {!member.is_me ? (
                    <Button variant="soft" size="sm" onClick={() => setRecipientId(member.user_id)}>
                      Message
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <aside className="space-y-6">
          <div className="rounded-3xl border border-border/70 bg-surface/40 p-5">
            <h2 className="font-display text-lg text-foreground">
              {recipient ? `Message ${recipient.display_name}` : "Start a conversation"}
            </h2>
            {!recipient ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Pick a teammate from the directory to start.
              </p>
            ) : (
              <>
                <Input
                  className="mt-3"
                  value={subject}
                  maxLength={140}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Subject"
                />
                <Textarea
                  className="mt-3"
                  rows={5}
                  value={body}
                  maxLength={4000}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder="Write your message…"
                />
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="hero"
                    size="sm"
                    disabled={sendMutation.isPending || !subject.trim() || !body.trim()}
                    onClick={() => sendMutation.mutate()}
                  >
                    Send
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setRecipientId(null)}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="rounded-3xl border border-border/70 bg-surface/40 p-5">
            <h2 className="font-display text-lg text-foreground">Conversations</h2>
            {messages.isLoading ? (
              <Skeleton className="mt-3 h-24 rounded-2xl" />
            ) : threads.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No messages yet.</p>
            ) : (
              <ul className="mt-3 space-y-4">
                {threads.map((thread) => {
                  const last = thread[thread.length - 1]!;
                  const otherId = last.sender_id === user?.id ? last.recipient_id : last.sender_id;
                  const unread = thread.some((m) => m.recipient_id === user?.id && !m.read_at);
                  return (
                    <li
                      key={last.thread_id}
                      className={cn(
                        "rounded-2xl border border-border/60 bg-background/40 p-3",
                        unread && "border-primary/60",
                      )}
                    >
                      <p className="text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                        With {nameFor(otherId)} · {thread.length}{" "}
                        {thread.length === 1 ? "message" : "messages"}
                      </p>
                      <p className="mt-1 text-sm font-medium text-foreground">{thread[0]!.subject}</p>

                      <ol className="mt-2 space-y-2 border-l border-border/60 pl-3">
                        {thread.map((message) => {
                          const incoming = message.recipient_id === user?.id;
                          return (
                            <li key={message.id}>
                              <p className="text-[0.7rem] text-muted-foreground">
                                {incoming ? nameFor(message.sender_id) : "You"} ·{" "}
                                {new Date(message.created_at).toLocaleString()}
                              </p>
                              <p className="whitespace-pre-line text-sm text-muted-foreground">
                                {message.body}
                              </p>
                              {incoming && !message.read_at ? (
                                <button
                                  type="button"
                                  className="mt-1 text-xs text-primary-glow hover:underline"
                                  onClick={() => readMutation.mutate(message.id)}
                                >
                                  Mark as read
                                </button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ol>

                      {replyThread === last.thread_id ? (
                        <div className="mt-3">
                          <Textarea
                            rows={3}
                            value={replyBody}
                            maxLength={4000}
                            onChange={(event) => setReplyBody(event.target.value)}
                            placeholder="Write a reply…"
                          />
                          <div className="mt-2 flex gap-2">
                            <Button
                              variant="hero"
                              size="sm"
                              disabled={replyMutation.isPending || !replyBody.trim()}
                              onClick={() =>
                                replyMutation.mutate({
                                  recipientId: otherId,
                                  subject: thread[0]!.subject,
                                  threadId: last.thread_id,
                                  parentId: last.id,
                                })
                              }
                            >
                              Send reply
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setReplyThread(null);
                                setReplyBody("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="mt-2 text-xs text-primary-glow hover:underline"
                          onClick={() => {
                            setReplyThread(last.thread_id);
                            setReplyBody("");
                          }}
                        >
                          Reply
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
